import type { PmTilesSourceId } from "@designboticteam/map";
import type { MapLibreInstanceService } from "../../../../core/MapLibreInstanceService";

/**
 *
 */
class DisplaySettingsController {
	private mapLibreInstanceService: MapLibreInstanceService;

	constructor({ mapLibreInstanceService }: { mapLibreInstanceService: MapLibreInstanceService }) {
		this.mapLibreInstanceService = mapLibreInstanceService;
	}

	/**
	 * Set opacity of specific source.
	 * Using global state property to keep original opacity values for stateful style expressions (e.g. idle / hover).
	 * Passed `value` works as multiplier of original (current) value.
	 */
	public setOpacity({ sourceId, value }: { sourceId: string; value: number }): void {
		this.mapLibreInstanceService.map.setGlobalStateProperty(`opacity__${sourceId}`, value);
	}

	/**
	 * Set visibility of all layers of specific source.
	 */
	public setVisibility({ sourceId, isVisible }: { sourceId: string; isVisible: boolean }): void {
		const map = this.mapLibreInstanceService.map;
		const visibility = isVisible ? "visible" : "none";

		for (const layerId of this.layerIdsOf(sourceId)) {
			if (map.getLayer(layerId) !== undefined) {
				map.setLayoutProperty(layerId, "visibility", visibility);
			}
		}
	}

	/**
	 * Set drawing order of layers in specific order.
	 *
	 * `orderedSourceIds` - top-to-bottom.
	 * `moveLayer` without `beforeId` always move the layer to the top.
	 * Iterating in reverse leaves the first-listed source on top, so UI order matches map order.
	 */
	public setOrder(orderedSourceIds: ReadonlyArray<PmTilesSourceId>): void {
		const map = this.mapLibreInstanceService.map;

		for (let i = orderedSourceIds.length - 1; i >= 0; i--) {
			for (const layerId of this.layerIdsOf(orderedSourceIds[i])) {
				if (map.getLayer(layerId) !== undefined) {
					map.moveLayer(layerId);
				}
			}
		}
	}

	// Returns every layer id a source could own: fill + outline for vector, raster for raster.
	// Callers only ever touch the few that actually exist, so listing all of them is harmless.
	private layerIdsOf(sourceId: string): [string, string, string] {
		return [`${sourceId}__fill`, `${sourceId}__outline`, `${sourceId}__raster`];
	}
}

export { DisplaySettingsController };
